/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    TextField,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    Switch,
    MenuItem,
    Collapse,
    IconButton,
    Typography,
    CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useClientAlerts } from '../../context/administration/ClientAlertsContext';
import { DialogMode } from '../../types/DialogTypes';

export type AlertDialogFields = {
    id?: number;
    title: string;
    numberPlate?: string;
    message?: string;
    ownerMsg?: string;
    targetNewCustomers?: boolean;
    executeIfStaffInLocation?: boolean;
    runOnlyOncePerCustomerOrStaff?: boolean;
    automaticallyMarkAsExecuted?: boolean;
};

interface AlertDialogProps {
    open: boolean;
    onClose: () => void;
    mode?: DialogMode;
    initialValues?: Partial<AlertDialogFields>;
};

const DEFAULT_FIELDS: AlertDialogFields = {
    title: '',
    numberPlate: '',
    message: '',
    ownerMsg: '',
    targetNewCustomers: false,
    executeIfStaffInLocation: true,
    runOnlyOncePerCustomerOrStaff: true,
    automaticallyMarkAsExecuted: false,
};

function AlertDialog({
    open,
    onClose,
    mode = DialogMode.CREATE,
    initialValues,
}: AlertDialogProps) {
    const { t } = useTranslation("common");
    const { handleUpdateAlert, handleCreateAlert } = useClientAlerts();

    const [loading, setLoading] = useState(false);
    const isViewMode = mode === DialogMode.VIEW;
    const isEditMode = mode === DialogMode.EDIT;
    const [advancedOpen, setAdvancedOpen] = useState(isViewMode || isEditMode);

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<AlertDialogFields>({
        defaultValues: { ...DEFAULT_FIELDS, ...initialValues },
    });

    // Reset form whenever the dialog opens
    useEffect(() => {
        if (open) {
            reset({ ...DEFAULT_FIELDS, ...initialValues });
            setAdvancedOpen(isViewMode || isEditMode);
            setLoading(false);
        }
    }, [open, isViewMode, isEditMode, reset, initialValues]);

    const targetNewCustomers = watch('targetNewCustomers');

    const dialogTitle = useMemo(() => {
        switch (mode) {
            case DialogMode.VIEW:
                return t('administration.clientAlerts.dialog.title.view-alert');
            case DialogMode.EDIT:
                return t('administration.clientAlerts.dialog.title.edit-alert');
            case DialogMode.CREATE:
            default:
                return t('administration.clientAlerts.dialog.title.create-alert');
        }
    }, [mode, t]);

    const buildPayload = (fields: AlertDialogFields) => ({
        name: fields.title,
        targetLicensePlate: fields.targetNewCustomers ? null : fields.numberPlate,
        targetNewCustomers: !!fields.targetNewCustomers,
        executeOnlyIfStaffAvailable: !!fields.executeIfStaffInLocation,
        runOnlyOncePerCustomerOrStaff: !!fields.runOnlyOncePerCustomerOrStaff,
        smsToOwners: fields.ownerMsg,
        smsToAvailableStaff: fields.message,
        automaticallyMarkAsExecuted: !!fields.automaticallyMarkAsExecuted,
    });

    const onSubmit = handleSubmit(async (fields) => {
        const payload = buildPayload(fields);
        setLoading(true);
        try {
            if (isEditMode && initialValues?.id) {
                await handleUpdateAlert({ ...payload, id: initialValues.id });
            } else {
                await handleCreateAlert(payload);
            }
            onClose();
        } finally {
            setLoading(false);
        }
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <Box component="form" display="flex" flexDirection="column" gap={2} mt={1} onSubmit={onSubmit}>
                    <Controller
                        name="title"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t('createAlertDialog.alertTitle')}
                                error={!!errors.title}
                                helperText={errors.title ? t('createAlertDialog.validation.title.required') : ' '}
                                disabled={isViewMode}
                                fullWidth
                                variant="outlined"
                            />
                        )}
                    />
                    <Controller
                        name="targetNewCustomers"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!field.value}
                                        onChange={e => field.onChange(e.target.checked)}
                                        disabled={isViewMode}
                                    />
                                }
                                label={t('createAlertDialog.targetNewCustomers')}
                            />
                        )}
                    />
                    {!targetNewCustomers && (
                        <Controller
                            name="numberPlate"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('createAlertDialog.numberPlate')}
                                    fullWidth
                                    variant="outlined"
                                    disabled={isViewMode || (!!initialValues?.numberPlate && !isEditMode)}
                                />
                            )}
                        />
                    )}
                    <Controller
                        name="message"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t('createAlertDialog.message')}
                                fullWidth
                                multiline
                                minRows={3}
                                variant="outlined"
                                disabled={isViewMode}
                            />
                        )}
                    />

                    <Box display="flex" alignItems="center" mt={2}>
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                            {t('createAlertDialog.advancedSettings')}
                        </Typography>
                        {!isViewMode && (
                            <IconButton onClick={() => setAdvancedOpen(prev => !prev)} size="small">
                                {advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        )}
                    </Box>

                    <Collapse in={advancedOpen}>
                        <Box display="flex" flexDirection="column" gap={2} mt={2}>
                            <Controller
                                name="executeIfStaffInLocation"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.executeIfStaffInLocation}>
                                        <InputLabel>{t('createAlertDialog.executeIfStaffInLocation')}</InputLabel>
                                        <Select
                                            {...field}
                                            value={field.value ? 1 : 0}
                                            label={t('createAlertDialog.executeIfStaffInLocation')}
                                            onChange={e => field.onChange(!!+e.target.value)}
                                            disabled={isViewMode}
                                        >
                                            <MenuItem value={1}>{t('createAlertDialog.executeIfStaffInLocation.true')}</MenuItem>
                                            <MenuItem value={0}>{t('createAlertDialog.executeIfStaffInLocation.false')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                name="runOnlyOncePerCustomerOrStaff"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>{t('createAlertDialog.runOnlyOncePerCustomerOrStaff')}</InputLabel>
                                        <Select
                                            {...field}
                                            value={field.value ? 1 : 0}
                                            label={t('createAlertDialog.runOnlyOncePerCustomerOrStaff')}
                                            onChange={e => field.onChange(!!+e.target.value)}
                                            disabled={isViewMode}
                                        >
                                            <MenuItem value={1}>{t('createAlertDialog.runOnlyOncePerCustomerOrStaff.true')}</MenuItem>
                                            <MenuItem value={0}>{t('createAlertDialog.runOnlyOncePerCustomerOrStaff.false')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                name="automaticallyMarkAsExecuted"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>{t('createAlertDialog.automaticallyMarkAsExecuted')}</InputLabel>
                                        <Select
                                            {...field}
                                            value={field.value ? 1 : 0}
                                            label={t('createAlertDialog.automaticallyMarkAsExecuted')}
                                            onChange={e => field.onChange(!!+e.target.value)}
                                            disabled={isViewMode}
                                        >
                                            <MenuItem value={1}>{t('createAlertDialog.automaticallyMarkAsExecuted.true')}</MenuItem>
                                            <MenuItem value={0}>{t('createAlertDialog.automaticallyMarkAsExecuted.false')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                name="ownerMsg"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t('createAlertDialog.messageForOwner')}
                                        fullWidth
                                        variant="outlined"
                                        disabled={isViewMode}
                                    />
                                )}
                            />
                        </Box>
                    </Collapse>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{isViewMode ? t('common.close') : t('common.cancel')}</Button>
                {!isViewMode && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={onSubmit}
                        disabled={loading}
                    >
                        {loading
                            ? <CircularProgress size={24} />
                            : (isEditMode ? t('common.save') : t('createAlertDialog.addAlert'))
                        }
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default AlertDialog;
